import { getRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import type FloorPlan from '../../dashboard/models/floor-plan';

export const getFloorPlansForOffice = async (
  officeId: string,
  keycloak: KeycloakContext,
): Promise<FloorPlan[] | undefined> => {
  const apiResponse = await getRequest<FloorPlan[]>(`floorplan?officeId=${officeId}`, keycloak);
  if (apiResponse.error) {
    return undefined;
  }
  return apiResponse.json;
};

export const getUnavailableWorkplaces = async (
  officeId: string,
  date: string,
  dayPart: number,
  keycloak: KeycloakContext,
  /**
   * Ask for the bookings of a day part that has already elapsed today.
   *
   * The server drops those by default: once this morning is over, a desk booked for this morning
   * stops blocking the plan for somebody choosing a seat. An administrator checking people in is
   * not choosing a seat - they are looking at where people are actually sitting - so the check-in
   * floorplan asks for them, and a desk somebody holds is drawn as held.
   */
  includeElapsedDayPart = false,
): Promise<string[] | undefined> => {
  const query = new URLSearchParams({
    officeId,
    date,
    dayPart: String(dayPart),
    ...(includeElapsedDayPart ? { includeElapsedDayPart: 'true' } : {}),
  });

  const apiResponse = await getRequest<string[]>(
    `floorplan/unavailable-workplaces?${query.toString()}`,
    keycloak,
  );
  if (apiResponse.error) {
    return undefined;
  }
  return apiResponse.json;
};

/**
 * What put somebody on a desk.
 *
 * The three are not the same promise: a `Reservation` is that person's own booking of that
 * exact desk, a `GroupReservation` holds a block of desks without recording who takes which
 * seat, and a `CheckIn` is somebody who walked up and scanned the QR code.
 */
export type WorkplaceOccupantSource = 'Reservation' | 'GroupReservation' | 'CheckIn';

/** One person on a desk, with whatever the office knows about reaching them. */
export interface WorkplaceOccupant {
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  /** Absent for a legacy check-in with no agenda entry behind it: the desk was used by nobody nameable. */
  readonly email?: string | null;
  /** The number the employee shared with this office; most have none. */
  readonly phoneNumber?: string | null;
  readonly avatarUrl?: string | null;
  readonly source: WorkplaceOccupantSource;
  /**
   * Whether this person is a company medic in this office.
   *
   * Sent to every viewer, unlike the contact details: who to fetch when somebody is hurt is the
   * one fact about a colleague that matters most to the people with no administrative rights.
   * Optional so an older server, which does not send it, reads as "not a medic" rather than as a
   * type error.
   */
  readonly isCompanyMedic?: boolean;
  /**
   * The group reservation this person is on the desk through; null for their own booking or
   * their own check-in.
   *
   * A group reservation holds a block of desks and records nothing about who takes which seat, so
   * every member is reported against every desk. This is what tells one block from another. It
   * does not say which desk of the block anybody is at - nothing does. See `medic-desks.ts`.
   */
  readonly groupReservationId?: string | null;
}

/** One occupied desk, and everybody the data puts on it for the half-day asked about. */
export interface WorkplaceOccupants {
  readonly workplaceId: string;
  readonly roomName?: string | null;
  /** Usually one. More than one only for a group reservation, which does not assign seats. */
  readonly occupants: readonly WorkplaceOccupant[];
}

export interface WorkplaceOccupantsResponse {
  readonly officeId: string;
  readonly date: string;
  readonly workplaces: readonly WorkplaceOccupants[];
  /**
   * Whether the occupants carry email addresses and phone numbers at all.
   *
   * The server sends those only to an office manager or administrator, which every caller of this
   * screen is — so this is true here in practice. Read rather than assumed, so a card does not
   * render an empty contact row that reads as "this colleague shared no number".
   */
  readonly containsContactDetails: boolean;
}

/**
 * Who is sitting where, for one office on one date and half-day.
 *
 * <p>
 * The companion of `getUnavailableWorkplaces`: that call says which desks are taken, this one
 * says by whom. Free desks are absent from the answer rather than present and empty, and the
 * two calls agree desk for desk as long as `includeElapsedDayPart` matches — pass the same
 * value to both or a desk drawn as booked will hover as empty once its half-day is over.
 * </p>
 *
 * <p>
 * Any member of the office may ask, and only an office manager or administrator is sent contact
 * details — the response says which answer it is in `containsContactDetails`. A caller the office
 * does not admit gets a 401, which the hook above this treats as "no details to show" rather than
 * as a failure: the floorplan itself still works.
 * </p>
 */
export const getWorkplaceOccupants = async (
  officeId: string,
  date: string,
  dayPart: number,
  keycloak: KeycloakContext,
  includeElapsedDayPart = false,
): Promise<WorkplaceOccupantsResponse | undefined> => {
  const query = new URLSearchParams({
    officeId,
    date,
    dayPart: String(dayPart),
    ...(includeElapsedDayPart ? { includeElapsedDayPart: 'true' } : {}),
  });

  const apiResponse = await getRequest<WorkplaceOccupantsResponse>(
    `floorplan/workplace-occupants?${query.toString()}`,
    keycloak,
  );
  if (apiResponse.error) {
    return undefined;
  }
  return apiResponse.json;
};
